import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SwatchIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { useBackground } from '../../contexts/BackgroundContext';
import { useLocation } from 'react-router-dom';

const BackgroundControls: React.FC = () => {
  const { settings, cycleBackground, cycleOverlay, getCurrentOverlay, setPlaybackSpeed, isEasterEggActive } = useBackground();
  const location = useLocation();
  
  // Initialize theme state and apply to DOM
  const [darkMode, setDarkMode] = useState(() => {
    // If easter egg is not active, force dark mode
    if (!isEasterEggActive) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      return true;
    }
    
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    let isDark: boolean;
    if (savedTheme === 'dark') {
      isDark = true;
    } else if (savedTheme === 'light') {
      isDark = false;
    } else {
      // Default to dark mode instead of system preference
      isDark = true;
      localStorage.setItem('theme', 'dark');
    }
    
    // Apply theme immediately to DOM
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    return isDark;
  });
  
  // Handle easter egg state changes
  useEffect(() => {
    if (!isEasterEggActive) {
      // Force dark mode when easter egg is deactivated
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setDarkMode(true);
    }
  }, [isEasterEggActive]);
  
  const toggleTheme = () => {
    // Only allow theme switching when easter egg is active
    if (!isEasterEggActive) {
      return;
    }
    
    const newDarkMode = !darkMode;
    
    // Update DOM immediately
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
    
    // Update state
    setDarkMode(newDarkMode);
  };
  
  // Don't show controls on auth page
  if (location.pathname === '/auth') {
    return null;
  }

  // Don't show any controls unless easter egg is active
  if (!isEasterEggActive) {
    /* COMMENTED OUT: Theme toggle button that was previously shown when easter egg was not active
    return (
      <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2">
        <motion.button
          onClick={toggleTheme}
          className="p-3 rounded-full bg-white/10 dark:bg-black/20 backdrop-blur-sm border border-white/20 dark:border-white/30 hover:bg-white/20 dark:hover:bg-black/30 transition-all duration-300 text-theme-text-primary dark:text-dark-theme-text-primary shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {darkMode ? (
            // Sun icon for light mode
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
            </svg>
          ) : (
            // Moon icon for dark mode
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
        </motion.button>
      </div>
    );
    */
    return null;
  }

  const getBackgroundName = () => {
    const names = {
      purple: 'Purple',
      hexagon: 'Hexagon',
      cyber: 'Cyber',
      flowing: 'Flowing',
      hitech: 'Hi-Tech',
      honeycomb: 'Honeycomb',
      off: 'Off',
    };
    return names[settings.backgroundType];
  };

  const getOverlayName = () => {
    const names = {
      // Dark mode overlays
      default: 'Default',
      dark: 'Dark',
      blue: 'Blue',
      minimal: 'Minimal',
      // Light mode overlays
      'light-default': 'Light',
      'light-bright': 'Bright',
      'light-warm': 'Warm',
      'light-minimal': 'Minimal',
      // Common
      none: 'None',
    };
    return names[getCurrentOverlay()];
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2">
      {/* Theme Toggle Button */}
      <motion.button
        onClick={toggleTheme}
        className="p-3 rounded-full bg-white/10 dark:bg-black/20 backdrop-blur-sm border border-white/20 dark:border-white/30 hover:bg-white/20 dark:hover:bg-black/30 transition-all duration-300 text-theme-text-primary dark:text-dark-theme-text-primary shadow-lg"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {darkMode ? (
          // Sun icon for light mode
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
          </svg>
        ) : (
          // Moon icon for dark mode
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
          </svg>
        )}
      </motion.button>

      {/* Background Switcher Button */}
      <motion.button
        onClick={cycleBackground}
        className="p-3 rounded-full bg-white/10 dark:bg-black/20 backdrop-blur-sm border border-white/20 dark:border-white/30 hover:bg-white/20 dark:hover:bg-black/30 transition-all duration-300 text-theme-text-primary dark:text-dark-theme-text-primary shadow-lg"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title={`Background: ${getBackgroundName()}`}
      >
        <SwatchIcon className="w-5 h-5" />
      </motion.button>

      {/* Overlay Selector Button */}
      <motion.button
        onClick={cycleOverlay}
        className="p-3 rounded-full bg-white/10 dark:bg-black/20 backdrop-blur-sm border border-white/20 dark:border-white/30 hover:bg-white/20 dark:hover:bg-black/30 transition-all duration-300 text-theme-text-primary dark:text-dark-theme-text-primary shadow-lg"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title={`Overlay: ${getOverlayName()}`}
      >
        <AdjustmentsHorizontalIcon className="w-5 h-5" />
      </motion.button>

      {/* Playback Speed Slider */}
      <motion.div
        className="p-3 rounded-full bg-white/10 dark:bg-black/20 backdrop-blur-sm border border-white/20 dark:border-white/30 shadow-lg w-12 h-12 flex items-center justify-center"
        whileHover={{ scale: 1.05 }}
        title={`Speed: ${Math.round(settings.playbackSpeed * 100)}%`}
      >
        <input
          type="range"
          min="0.1"
          max="2"
          step="0.1"
          value={settings.playbackSpeed}
          onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
          className="w-6 h-1 bg-gray-300/50 rounded-lg appearance-none cursor-pointer"
          style={{
            writingMode: 'vertical-lr' as const,
            WebkitAppearance: 'slider-vertical',
            transform: 'rotate(-90deg)',
            transformOrigin: 'center'
          }}
        />
      </motion.div>
    </div>
  );
};

export default BackgroundControls;